<template>
  <div class="re-review-page">
    <!-- 顶部分类导航栏 -->
    <div class="category-nav">
      <a-menu
        mode="horizontal"
        :selected-keys="[selectedCategory]"
        class="nav-menu"
        @click="handleCategoryChange"
      >
        <a-menu-item
          v-for="category in categories"
          :key="category.value"
          class="nav-item"
        >
          {{ category.name }}
        </a-menu-item>
      </a-menu>
    </div>

    <!-- 卡片数据流展示区域 -->
    <div ref="cardContainer" class="card-container">
      <div class="card-grid">
        <a-card
          v-for="item in products"
          :key="item.id"
          hoverable
          class="product-card"
          :body-style="{ padding: '12px' }"
          @click="handleCardClick(item)"
        >
          <template #cover>
            <div class="image-container">
              <img
                :src="getFirstImage(item)"
                :alt="item.name"
                class="product-image"
                @error="handleImageError"
              >
            </div>
          </template>
          <div class="card-content">
            <h3 class="product-name">{{ item.name_cn || item.name }}</h3>
            <p class="product-id">编号: {{ item.pi_code || item.id }}</p>
          </div>
        </a-card>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <a-spin size="large" />
      </div>
    </div>
  </div>
</template>

<script>
import { getReReviewCategories, getReReviewProducts } from '@/api/reReview'

export default {
  name: 'ReReviewPage',
  data() {
    return {
      categories: [],
      selectedCategory: '',
      products: [],
      loading: false,
      hasMore: true,
      currentPage: 1,
      pageSize: 20
    }
  },
  computed: {
    // 根据路由meta信息判断是否为只读模式
    isReadonly() {
      return this.$route.meta && this.$route.meta.readonly
    }
  },
  mounted() {
    this.initPage()
    this.bindScrollEvent()
  },
  beforeDestroy() {
    this.unbindScrollEvent()
  },
  methods: {
    async initPage() {
      try {
        await this.loadCategories()
        if (this.categories.length > 0) {
          this.selectedCategory = this.categories[0].value
          await this.loadProducts(true)
        }
      } catch (error) {
        this.$message.error('页面初始化失败')
        console.error('页面初始化失败:', error)
      }
    },

    async loadCategories() {
      try {
        console.log('开始加载分类...')
        const response = await getReReviewCategories()
        console.log('分类API响应:', response)
        this.categories = response.lists || []
        console.log('分类数据:', this.categories)
      } catch (error) {
        console.error('加载分类失败:', error)
        throw error
      }
    },

    async loadProducts(reset = false) {
      if (this.loading) return

      this.loading = true

      try {
        const params = {
          category_id: this.selectedCategory,
          page: reset ? 1 : this.currentPage,
          limit: this.pageSize
        }

        console.log('开始加载产品，参数:', params)
        const response = await getReReviewProducts(params)
        console.log('产品API响应:', response)
        const newProducts = response.data || []
        console.log('产品数据:', newProducts)

        if (reset) {
          this.products = newProducts
          this.currentPage = 1
        } else {
          this.products = [...this.products, ...newProducts]
        }

        this.hasMore = newProducts.length === this.pageSize
        this.currentPage++
      } catch (error) {
        this.$message.error('加载数据失败')
        console.error('加载产品数据失败:', error)
      } finally {
        this.loading = false
      }
    },

    handleCategoryChange({ key }) {
      this.selectedCategory = key
      this.currentPage = 1
      this.hasMore = true
      this.loadProducts(true)
    },

    getFirstImage(item) {
      if (item.image && item.image.length > 0) {
        return item.image[0].thumb_image_url || item.image[0].whole_image_url
      }
      if (item.images && item.images.length > 0) {
        return item.images[0]
      }
      // 默认占位图
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjYwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDI2MCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyNjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0xMzAgOTBMMTEwIDExMEgxNTBMMTMwIDkwWiIgZmlsbD0iI0Q5RDlEOSIvPgo8L3N2Zz4K'
    },

    handleImageError(event) {
      event.target.src = this.getFirstImage({})
    },

    bindScrollEvent() {
      window.addEventListener('scroll', this.handleScroll)
    },

    unbindScrollEvent() {
      window.removeEventListener('scroll', this.handleScroll)
    },

    handleScroll() {
      if (this.loading || !this.hasMore) return

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight

      // 距离底部100px时开始加载
      if (scrollTop + windowHeight >= documentHeight - 100) {
        this.loadProducts()
      }
    },

    handleCardClick(item) {
      // 根据是否为只读模式决定跳转路径
      if (this.isReadonly) {
        // 终评作品只能查看，跳转到只读详情页
        this.$router.push({ 
          path: '/admin/reReviewDetail', 
          query: { 
            id: item.id, 
            readonly: true 
          }
        })
      } else {
        // 复评模式，跳转到可操作的详情页
        this.$router.push({ 
          path: '/admin/reReviewDetail', 
          query: { 
            id: item.id 
          }
        })
      }
    }
  }
}
</script>

<style scoped>
.re-review-page {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.category-nav {
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}

.nav-menu {
  border-bottom: none;
  justify-content: center;
  max-width: 1200px;
  margin: 0 auto;
}

.nav-item {
  font-size: 16px;
  font-weight: 500;
  margin: 0 20px;
}

.card-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  width: 100%;
}

@media (min-width: 1400px) {
  .card-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}

@media (min-width: 1200px) and (max-width: 1399px) {
  .card-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (min-width: 768px) and (max-width: 1199px) {
  .card-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 480px) and (max-width: 767px) {
  .card-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 479px) {
  .card-grid {
    grid-template-columns: 1fr;
  }
}

.product-card {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.product-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.image-container {
  width: 100%;
  overflow: hidden;
  border-radius: 6px 6px 0 0;
}

.product-image {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.product-image:hover {
  transform: scale(1.05);
}

.card-content {
  padding: 12px;
}

.product-name {
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 8px 0;
  line-height: 1.4;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.product-id {
  font-size: 12px;
  color: #666;
  margin: 0;
}

.loading-container {
  text-align: center;
  padding: 40px 0;
}

.no-more-data {
  text-align: center;
  padding: 20px 0;
  color: #999;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .card-container {
    padding: 16px;
  }

  .card-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
  }

  .product-card {
    width: 100%;
    max-width: 260px;
  }

  .nav-item {
    margin: 0 10px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .card-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .nav-menu {
    overflow-x: auto;
  }

  .nav-item {
    margin: 0 8px;
    white-space: nowrap;
  }
}
</style>
